import { DefaultEpilotTheme, EpilotTheme } from '@epilot/base-elements'
import { Page } from '@epilot/customer-portal-client'
import merge from 'lodash/fp/merge'

import { type Meter } from 'api/MeteringApiClient'
import { ExternalLink } from 'context/PortalConfigContext'
import { Contract } from 'utils/ContractUtils'

import { BillingEvent, EntityItem, Order } from '../api/ApiTypes'
import { Opportunity } from '../utils/OpportunityUtils'

export const MOCK_ORDER: EntityItem = {
  _id: 'id',
  metadata: null,
  _tags: [],
  status: 'active',
  order_number: 'Order 323',
  billing_first_name: 'John',
  billing_last_name: 'Doe',
  billing_email: 'john.doe@example.com',
  billing_phone: '1234567890',
  billing_address: [
    {
      street: 'Main Street',
      street_number: '123',
      city: 'Berlin',
      postal_code: '10115',
      country: 'Germany',
      _tags: []
    }
  ],
  delivery_address: [
    {
      street: 'Secondary Street',
      street_number: '456',
      city: 'Berlin',
      postal_code: '10115',
      country: 'Germany',
      _tags: []
    }
  ],
  payment_method: [
    {
      type: 'PayPal'
    }
  ],
  currency: 'EUR',
  line_items: [],
  amount_subtotal: 12300,
  amount_total: 13000,
  _schema: 'contract',
  _org: 'org',
  _created_at: '2021-10-31T01:30:00.000-05:00',
  _updated_at: '2021-11-31T01:30:00.000-05:00',
  _title: 'Order Nr.12020',
  billing_contact: {
    $relation: [
      {
        entity_id: '12',
        relationText: '',
        _tags: [],
        _schema: 'contract'
      }
    ]
  },
  org: {
    id: 'org_id'
  }
}

export const MOCK_OPPORTUNITY: Opportunity = {
  _id: '96b94f5b-f99b-452d-93a8-f6f1303a74c4',
  status: 'lead',
  billing_address: [
    {
      _id: '96b94f5b-f99b-452d-93a8-f6f1303a74c4',
      street: 'Sample Street',
      street_number: '12345',
      city: 'Sample City',
      postal_code: '12345',
      country: 'de',
      _tags: []
    }
  ],
  delivery_address: [
    {
      _id: '96b94f5b-f99b-452d-93a8-f6f1303a74c4',
      street: 'Sample Street',
      street_number: '12345',
      city: 'Sample City',
      postal_code: '12345',
      country: 'de',
      _tags: []
    }
  ],
  items: [],
  opportunity_number: 'OPZU7iDeF',
  _schema: 'opportunity',
  _org: 'test-org',
  _created_at: '2022-02-04T10:24:29.288Z',
  _updated_at: '2022-02-04T12:05:03.073Z',
  _title: 'Opportunity 23',
  journey_data: {
    'Zahlung/color': 'red'
  },
  customer: null,
  opportunity_title: 'Opportunity 23',
  latestQuote: MOCK_ORDER,
  _tags: null,
  _files: [],
  files: [],
  org: null,
  currency: 'EUR',
  _purpose: []
}

export const MOCK_OPPORTUNITIES = [
  {
    _id: '1',
    _title: 'Opportunity 1',
    status: 'in_progress',
    _schema: 'opportunity',
    _created_at: '2021-01-01',
    _updated_at: '2021-01-01'
  },
  {
    _id: '2',
    _title: 'Opportunity 2',
    status: 'active',
    _created_at: '2021-01-01',
    _schema: 'opportunity',
    _updated_at: '2021-01-01'
  },
  {
    _id: '3',
    _title: 'Opportunity 3',
    status: 'in_progress',
    _created_at: '2021-01-01',
    _schema: 'opportunity',
    _updated_at: '2021-01-01'
  },
  {
    _id: '4',
    _title: 'Opportunity 4',
    status: 'in_progress',
    _schema: 'opportunity',
    _created_at: '2021-01-01',
    _updated_at: '2021-01-01'
  }
] as Opportunity[]

export const MOCK_ORDERS = [
  {
    _id: '1',
    _title: 'Order 1',
    status: 'quote',
    _schema: 'order',
    _created_at: '2023-01-01',
    _updated_at: '2023-01-01'
  },
  {
    _id: '2',
    _title: 'Order 2',
    status: 'placed',
    _schema: 'order',
    _created_at: '2023-01-01',
    _updated_at: '2021-01-01'
  },
  {
    _id: '3',
    _title: 'Order 3',
    status: 'quote',
    _schema: 'order',
    _created_at: '2023-01-01',
    _updated_at: '2023-01-01'
  },
  {
    _id: '4',
    _title: 'Order 4',
    status: 'placed',
    _schema: 'order',
    _created_at: '2023-01-01',
    _updated_at: '2023-01-01'
  }
] as Order[]

export const MOCK_EXTERNAL_LINKS = [
  {
    id: '1',
    label: {
      en: 'View latest electricity bill',
      de: 'Letzte Stromrechnung anzeigen'
    },
    link: 'https://customer.energyprovider.com/billing/latest'
  },
  {
    id: '2',
    label: {
      en: 'Submit meter reading',
      de: 'Zählerstand übermitteln'
    },
    link: 'https://customer.energyprovider.com/meter-reading'
  },
  {
    id: '3',
    label: {
      en: 'Compare energy tariffs',
      de: 'Energiepreise vergleichen'
    },
    link: 'https://customer.energyprovider.com/tariffs'
  }
] as ExternalLink[]

export const MOCK_CONTRACTS = [
  {
    _id: '1',
    _org: '123',
    _title: 'Contract 1',
    status: 'active',
    _schema: 'contract',
    _created_at: '2023-03-06',
    _updated_at: '2023-03-06'
  },
  {
    _id: '2',
    _org: '123',
    _title: 'Contract 2',
    status: 'active',
    _schema: 'contract',
    _created_at: '2023-04-14',
    _updated_at: '2023-03-06'
  },
  {
    _id: '3',
    _org: '123',
    _title: 'Contract 3',
    status: 'active',
    _schema: 'contract',
    _created_at: '2023-04-03',
    _updated_at: '2023-03-06'
  },
  {
    _id: '4',
    _org: '123',
    _title: 'Contract 4',
    status: 'active',
    _schema: 'contract',
    _created_at: '2023-07-09',
    _updated_at: '2023-03-06'
  }
] as Contract[]

export const MOCK_METERS = [
  {
    _id: '1',
    _title: 'Meter 1',
    sector: 'power'
  },
  {
    _id: '2',
    _title: 'Meter 2',
    sector: 'power'
  },
  {
    _id: '3',
    _title: 'Meter 3',
    sector: 'gas'
  },
  {
    _id: '4',
    _title: 'Meter 4',
    sector: 'gas'
  }
] as Meter[]

export const MOCK_BILLING_EVENTS: BillingEvent[] = [
  {
    type: 'installment',
    _id: '3ef5073d-bd7c-4577-a9f1-28f70516b755',
    _created_at: '2022-01-02T00:00:00.000Z',
    _updated_at: '2022-01-02T00:00:00.000Z',
    billing_amount: -10000,
    _title: 'Billing Event 1',
    _org: '123',
    billing_amount_decimal: '100.00',
    billing_currency: 'EUR',
    due_date: '2023-11-01',
    contract: {
      $relation: [
        {
          entity_id: 'ef957d2b-9cb4-467f-81c6-af874a34b1b6'
        }
      ]
    },
    external_id: 'external-id-1'
  },
  {
    type: 'installment',
    _id: '3ef5073d-bd7c-4577-a9f1-28f70516b755',
    _created_at: '2022-01-02T00:00:00.000Z',
    _updated_at: '2022-01-02T00:00:00.000Z',
    billing_amount: -10000,
    _title: 'Billing Event 2',
    _org: '123',
    billing_amount_decimal: '100.00',
    billing_currency: 'EUR',
    due_date: '2033-11-01',
    contract: {
      $relation: [
        {
          entity_id: 'ef957d2b-9cb4-467f-81c6-af874a34b1b6'
        }
      ]
    },
    external_id: 'external-id-2'
  },
  {
    type: 'installment',
    _id: '3ef5073d-bd7c-4577-a9f1-28f70516b755',
    _created_at: '2022-01-02T00:00:00.000Z',
    _updated_at: '2022-01-02T00:00:00.000Z',
    billing_amount: -10000,
    _title: 'Billing Event 3',
    _org: '123',
    billing_amount_decimal: '100.00',
    billing_currency: 'EUR',
    due_date: '2023-11-01',
    paid_date: '2023-10-01',
    contract: {
      $relation: [
        {
          entity_id: 'ef957d2b-9cb4-467f-81c6-af874a34b1b6'
        }
      ]
    },
    external_id: 'external-id-3'
  },
  {
    type: 'installment',
    _id: '3ef5073d-bd7c-4577-a9f1-28f70516b755',
    _created_at: '2022-01-02T00:00:00.000Z',
    _updated_at: '2022-01-02T00:00:00.000Z',
    billing_amount: -10000,
    _title: 'Billing Event 4',
    _org: '123',
    billing_amount_decimal: '100.00',
    billing_currency: 'EUR',
    due_date: '2023-11-01',
    paid_date: '2023-09-01',
    contract: {
      $relation: [
        {
          entity_id: 'ef957d2b-9cb4-467f-81c6-af874a34b1b6'
        }
      ]
    },
    external_id: 'external-id-4'
  }
]

export const ECP_THEME_MOCK: Required<EpilotTheme> = merge(DefaultEpilotTheme, {
  muiOverrides: { MuiPaper: { root: { backgroundColor: '#fff' } } },
  palette: {
    background: {
      default: '#f5f7fa'
    }
  }
}) as Required<EpilotTheme>

export const MOCK_PAGES_CONFIG: Page[] = [
  {
    slug: 'login',
    path: '/login',
    order: 1,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'aafddc2e-b8f9-4c18-b2df-660a1b02d0cf'
  },
  {
    slug: 'register',
    path: '/register',
    order: 2,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: '46efdd3f-0cf1-4562-9268-064203f9f935'
  },
  {
    slug: 'pending-account',
    path: '/pending-account',
    order: 3,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: '7721852b-8049-4d9c-b163-17d98e96cf80'
  },
  {
    slug: 'register-activation',
    path: '/register/activation',
    order: 4,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'a6e3efb1-3a00-4e7f-9c9c-2e0e6a597f8a'
  },
  {
    slug: 'register-success',
    path: '/register/success',
    order: 5,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'f0cb4915-38a2-4de6-9519-8459b9b2fc9d'
  },
  {
    slug: 'dashboard',
    path: '/dashboard',
    order: 6,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'b0b4a204-f402-4c09-b7e6-d3e0ed6c47c4'
  },
  {
    slug: 'contracts',
    path: '/contracts',
    order: 7,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'abc325e7-2804-438a-ae61-1d6e6f515623'
  },
  {
    slug: 'payments',
    path: '/payments',
    order: 8,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'e9d79880-1c52-474c-871c-bf0acddbc88a'
  },
  {
    slug: 'meters',
    path: '/meters',
    order: 9,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: '64fd65dc-c4b7-4643-ae26-62a617be7fcf'
  },
  {
    slug: 'my-account',
    path: '/my-account',
    order: 10,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'f6a51c77-2357-4ed5-b733-b9ed12179a6e'
  },
  {
    slug: 'requests',
    path: '/requests',
    order: 11,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: '30920e62-0f2d-4e45-aab9-92715c2bc528'
  },
  {
    slug: 'requests-opportunities-id',
    path: '/requests/opportunities/:id',
    order: 12,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'a7177489-e040-42e0-9e93-00f99e3033fd'
  },
  {
    slug: 'contract',
    path: '/contract',
    order: 13,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: '3ae3af64-0dc1-4c0e-865c-e667f53e45df'
  },
  {
    slug: 'requests-orders-id',
    path: '/requests/orders/:id',
    order: 13,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: '7e1b462f-d8d7-4a26-82eb-4e57d4d23509'
  },
  {
    slug: 'contract',
    path: '/contract',
    order: 13,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'b0458adf-5632-4e3c-a900-c7b6f791f9b2'
  },
  {
    slug: 'contract',
    path: '/contract',
    order: 13,
    org_id: '739224',
    domain: 'end-customer-portal.ecp.dev.epilot.io',
    id: 'e375e97f-24fc-434c-84f1-c51803bd7e75'
  }
]
